import { forwardRef, type ReactNode, type SelectHTMLAttributes } from 'react';

import { useFieldControl } from './fieldContext';
import styles from './control.module.css';

export interface SelectOption {
  readonly value: string;
  readonly label: string;
  readonly disabled?: boolean | undefined;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'className'> {
  /** Declarative option list. Ignored when `children` are provided. */
  options?: readonly SelectOption[] | undefined;
  /** Non-selectable first entry, e.g. "Selecione um fornecedor". */
  placeholder?: string | undefined;
  invalid?: boolean | undefined;
  className?: string | undefined;
  children?: ReactNode | undefined;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { options, placeholder, invalid, className, children, ...rest },
  ref,
) {
  const field = useFieldControl();
  const isInvalid = invalid ?? field?.invalid ?? false;

  return (
    <select
      {...rest}
      ref={ref}
      id={rest.id ?? field?.controlId}
      aria-describedby={rest['aria-describedby'] ?? field?.describedBy}
      aria-invalid={isInvalid || undefined}
      aria-required={(rest.required ?? field?.required) === true ? true : undefined}
      required={rest.required ?? field?.required ?? false}
      disabled={rest.disabled ?? field?.disabled ?? false}
      className={[styles.control, styles.select, className].filter(Boolean).join(' ')}
    >
      {placeholder !== undefined ? (
        <option value="" disabled>
          {placeholder}
        </option>
      ) : null}
      {children ??
        options?.map((option) => (
          <option key={option.value} value={option.value} disabled={option.disabled ?? false}>
            {option.label}
          </option>
        ))}
    </select>
  );
});
